/**
 * The exposure view ⇄ URL contract.
 *
 * The whole filter set lives in the URL for the reason the Downline page gives:
 * "look at these six" should be a link, not a list of instructions. It also
 * means the command palette can hand an operator a landing view — an event, a
 * search, a threshold — without this feature exporting a component.
 *
 * `basis` and `sort` are LENSES, not filters. They change how the same book is
 * valued and ordered; they remove nothing from it. So neither counts towards
 * "filters are on", neither is cleared by Clear filters, and neither triggers
 * the "these figures describe the events in view" caveat — a caveat that fired
 * on a re-sort would be a lie, since a sort hides nothing.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EXPOSURE_SORTS, MARKET_TYPE_BUCKETS, MARKET_TYPE_FILTERS, SPORT_FILTERS } from '@/app/queries'
import { money } from '@/lib/format'
import type * as api from '@/data/api'
import type { MarketType, Sport } from '@/data/types'

/**
 * Param names are a contract, not strings: the palette builds `/exposure?...`
 * links against them, so renaming one here has to break its call site.
 */
export const EXPOSURE_PARAMS = {
  basis: 'basis',
  q: 'q',
  sport: 'sport',
  type: 'type',
  live: 'live',
  worse: 'worse',
  sort: 'sort',
  event: 'event',
} as const

/** `main` and `fancy` are the synthetic buckets from `@/app/queries`. */
export type MarketTypeFilter = 'all' | 'main' | 'fancy' | MarketType

export interface ExposureView {
  basis: 'my' | 'total'
  q: string
  sports: Sport[]
  type: MarketTypeFilter
  live: boolean
  /** Magnitude floor in whole rupees, or null. */
  worse: number | null
  sort: api.ExposureSort
  eventId: string | null
}

export const DEFAULT_EXPOSURE_VIEW: ExposureView = {
  basis: 'total',
  q: '',
  sports: [],
  type: 'all',
  live: false,
  worse: null,
  sort: 'worst',
  eventId: null,
}

const P = EXPOSURE_PARAMS

/**
 * Defensive throughout: an unknown value falls back to the default rather than
 * throwing. A hand-edited link is a normal thing to receive, and a risk screen
 * that white-screens on `?sort=banana` is worse than one that ignores it.
 */
export function parseExposureView(params: URLSearchParams): ExposureView {
  const typeParam = params.get(P.type)
  const sortParam = params.get(P.sort)
  const worseParam = Number(params.get(P.worse))

  return {
    basis: params.get(P.basis) === 'my' ? 'my' : 'total',
    q: params.get(P.q) ?? '',
    sports: params
      .getAll(P.sport)
      .filter((value): value is Sport => (SPORT_FILTERS as string[]).includes(value)),
    type: MARKET_TYPE_FILTERS.some((filter) => filter.id === typeParam)
      ? (typeParam as MarketTypeFilter)
      : 'all',
    live: params.get(P.live) === '1',
    worse: Number.isFinite(worseParam) && worseParam > 0 ? Math.round(worseParam) : null,
    sort: EXPOSURE_SORTS.find((entry) => entry.id === sortParam)?.id ?? 'worst',
    eventId: params.get(P.event) || null,
  }
}

/** One definition of what a synthetic bucket means, shared with the palette. */
export function marketTypesOf(type: MarketTypeFilter): MarketType[] | undefined {
  if (type === 'all') return undefined
  if (type === 'main' || type === 'fancy') return MARKET_TYPE_BUCKETS[type]
  return [type]
}

/** The projection query behind a view. Sorting and filtering are both the data
 *  layer's job so the ordering is true across the whole book, not one page. */
export function exposureQueryOf(view: ExposureView): api.ExposureQuery {
  return {
    basis: view.basis,
    sports: view.sports.length > 0 ? view.sports : undefined,
    search: view.q.trim() || undefined,
    marketTypes: marketTypesOf(view.type),
    inPlayOnly: view.live || undefined,
    worseThan: view.worse ?? undefined,
    eventIds: view.eventId ? [view.eventId] : undefined,
    sort: view.sort,
  }
}

/** Only things that REMOVE events from the book count. See the module note. */
export function isFiltered(view: ExposureView): boolean {
  return (
    view.q.trim().length > 0 ||
    view.sports.length > 0 ||
    view.type !== 'all' ||
    view.live ||
    view.worse !== null ||
    view.eventId !== null
  )
}

/** Human list for the toolbar's live region and the empty state. */
export function describeFilters(view: ExposureView): string[] {
  const out: string[] = []
  if (view.q.trim()) out.push(`“${view.q.trim()}”`)
  if (view.sports.length > 0) out.push(view.sports.join(', '))
  if (view.type !== 'all') {
    out.push(MARKET_TYPE_FILTERS.find((filter) => filter.id === view.type)?.label ?? view.type)
  }
  if (view.live) out.push('in play only')
  if (view.worse !== null) out.push(`worse than ${money(view.worse, { round: true })}`)
  if (view.eventId) out.push('one event')
  return out
}

/** Writes a whole view onto a params bag, deleting first so a cleared value
 *  actually disappears rather than being appended to. */
function writeView(params: URLSearchParams, view: ExposureView): void {
  for (const key of Object.values(P)) params.delete(key)
  if (view.basis !== DEFAULT_EXPOSURE_VIEW.basis) params.set(P.basis, view.basis)
  if (view.q.trim()) params.set(P.q, view.q)
  for (const sport of view.sports) params.append(P.sport, sport)
  if (view.type !== 'all') params.set(P.type, view.type)
  if (view.live) params.set(P.live, '1')
  if (view.worse !== null) params.set(P.worse, String(view.worse))
  if (view.sort !== DEFAULT_EXPOSURE_VIEW.sort) params.set(P.sort, view.sort)
  if (view.eventId) params.set(P.event, view.eventId)
}

/**
 * A link to the exposure grid in some view — `exposureLink({ q: 'Trent Rockets' })`
 * → `/exposure?q=Trent+Rockets`. Exported so the palette never reimplements the
 * encoding and then drifts from it.
 */
export function exposureLink(patch: Partial<ExposureView> = {}): string {
  const params = new URLSearchParams()
  writeView(params, { ...DEFAULT_EXPOSURE_VIEW, ...patch })
  const query = params.toString()
  return query ? `/exposure?${query}` : '/exposure'
}

export interface ExposureViewControls {
  view: ExposureView
  /** Merge a partial view into the URL. */
  set: (next: Partial<ExposureView>) => void
  /** Everything back to default except the two lenses. */
  clearFilters: () => void
  isFiltered: boolean
  filterLabels: string[]
}

export function useExposureView(): ExposureViewControls {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = useMemo(() => parseExposureView(searchParams), [searchParams])

  const write = useCallback(
    (next: ExposureView) => {
      const params = new URLSearchParams(searchParams)
      writeView(params, next)
      // replace, not push: typing four characters into the search box must not
      // cost four presses of the back button.
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const set = useCallback(
    (next: Partial<ExposureView>) => write({ ...view, ...next }),
    [view, write],
  )

  const clearFilters = useCallback(
    () => write({ ...DEFAULT_EXPOSURE_VIEW, basis: view.basis, sort: view.sort }),
    [view.basis, view.sort, write],
  )

  return {
    view,
    set,
    clearFilters,
    isFiltered: isFiltered(view),
    filterLabels: describeFilters(view),
  }
}
